<template>
  <div>
    <BaseHeader
      :title="$t('ad-materials.header.title')"
      :style-variant="14"
    >
      <template #desc>
        <p>{{ $t('ad-materials.header.description') }}</p>
      </template>
      <template #content>
        <div class="d-flex flex-wrap ga-3 mx-4 mb-4 flex-column flex-md-row">
          <div class="d-flex flex-wrap flex-grow-1 ga-3">
            <v-badge
              :model-value="!!activeFiltersAmount"
              :content="activeFiltersAmount"
              color="red"
            >
              <v-btn
                variant="outlined"
                prepend-icon="mdi-filter-variant"
                color="primary"
                @click="handleFilterClick"
                density="compact"
                class="filter-btn"
              >
                {{ $t('ad-materials.button') }}
              </v-btn>
            </v-badge>
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              :placeholder="$t('ad-materials.inputs.search.placeholder')"
              variant="outlined"
              density="compact"
              color="primary"
              class="search-field"
              hide-details
            />
          </div>

          <div class="right-controls d-flex align-center">
            <Feature :featureSlug="featureNames.AD_MATERIALS_UPLOAD">
              <v-btn
                variant="outlined"
                color="primary"
                class="upload-btn text-uppercase text-none text--white px-4 mr-3"
                :class="{ 'mb-4': isMobile }"
                @click="handleUploadClick"
                rounded
              >
                <v-icon class="mr-2">mdi-upload</v-icon>
                {{ $t('ad-materials.upload.button') }}
              </v-btn>
            </Feature>
            <v-select
              v-model="selectedSort"
              :items="sortOptions"
              :label="$t('ad-materials.inputs.sort.label')"
              item-title="text"
              item-value="value"
              variant="outlined"
              density="compact"
              color="primary"
              class="select-field mr-3"
              hide-details
            />
          </div>
        </div>
      </template>
    </BaseHeader>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'

import BaseHeader from '@/components/BaseHeader.vue'
import { debounce } from '@/lib/debounce'
import featureNames from '@/lib/featureNames'
import { Feature } from '@/plugins/feature-config'

const { smAndDown: isMobile } = useDisplay()

const emit = defineEmits<{
  (e: 'filter', payload: { search: string; sort: string }): void
  (e: 'filterButtonClick'): void
  (e: 'uploadButtonClick'): void
}>()

const _props = withDefaults(
  defineProps<{
    activeFiltersAmount?: number
  }>(),
  {
    activeFiltersAmount: 0,
  }
)

const { t } = useI18n()

const searchQuery = ref('')
const selectedSort = ref('new-to-old')

const sortOptions = [
  { value: 'new-to-old', text: t('ad-materials.inputs.sort.options.new-to-old') },
  { value: 'old-to-new', text: t('ad-materials.inputs.sort.options.old-to-new') },
]

const emitFilter = () => {
  emit('filter', {
    search: searchQuery.value,
    sort: selectedSort.value,
  })
}

const debouncedEmitFilter = debounce(emitFilter, 300)

const handleFilterClick = () => {
  emit('filterButtonClick')
  emitFilter()
}

const handleUploadClick = () => {
  emit('uploadButtonClick')
}

watch([selectedSort], () => {
  emitFilter()
})

watch(searchQuery, () => {
  debouncedEmitFilter()
})
</script>

<style scoped>
.right-controls {
  width: auto;
}

.filter-btn {
  height: 40px;
  width: fit-content;
}

.upload-btn {
  height: 40px;
  width: fit-content;
}

.search-field {
  max-width: 300px;
}

.select-field {
  max-width: 180px;
}

@media (max-width: 600px) {
  .controls-container {
    flex-direction: column;
  }
  .search-field {
    width: 100% !important;
    max-width: unset;
  }

  .select-field {
    width: 100% !important;
    max-width: unset;
  }
  .right-controls {
    width: 100%;
    flex-wrap: wrap;
    margin-top: 8px;
  }
  .upload-btn {
    margin-top: 8px;
    width: 100%;
  }
}
</style>
